import React from "react";
import {
  FaUndo,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaClock,
  FaEnvelope,
} from "react-icons/fa";

const CancellationPolicy = () => {
  return (
    <div
      style={{
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        background: "linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)",
        minHeight: "100vh",
        paddingBottom: "40px",
      }}
    >
      {/* Enhanced Header */}
      <header
        style={{
          background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
          color: "#fff",
          padding: "50px 20px 35px 20px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative Circles */}
        <div
          style={{
            position: "absolute",
            top: "-50px",
            right: "-50px",
            width: "200px",
            height: "200px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-30px",
            left: "-30px",
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 1,
            maxWidth: "800px",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(255, 255, 255, 0.2)",
              padding: "6px 16px",
              borderRadius: "20px",
              marginBottom: "16px",
              fontSize: "0.85rem",
              fontWeight: "600",
            }}
          >
            <FaUndo style={{ fontSize: "0.9rem" }} />
            Trivana Hospitality
          </div>

          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "800",
              marginBottom: "12px",
              letterSpacing: "-0.8px",
              textShadow: "0 2px 10px rgba(0,0,0,0.1)",
            }}
          >
            Cancellation Policy
          </h1>

          <p
            style={{
              fontSize: "1rem",
              opacity: 0.95,
              fontWeight: "500",
              maxWidth: "700px",
              margin: "0 auto 12px auto",
              lineHeight: "1.6",
            }}
          >
            Understanding our cancellation and refund terms to help you make
            informed booking decisions with Booktrivana.
          </p>

          <div
            style={{
              fontSize: "0.85rem",
              opacity: 0.9,
              fontWeight: "500",
            }}
          >
            Effective date: September 5, 2025
          </div>
        </div>
      </header>

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "0 20px" }}>
        {/* Table of Contents */}
        <nav
          style={{
            background: "#FFFFFF",
            padding: "24px",
            borderRadius: "14px",
            margin: "35px 0",
            border: "1px solid rgba(238, 46, 36, 0.1)",
            boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
          }}
        >
          <h2
            style={{
              fontSize: "1.1rem",
              fontWeight: "800",
              color: "#2E2E2E",
              marginBottom: "16px",
              letterSpacing: "-0.5px",
            }}
          >
            Table of Contents
          </h2>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "10px",
            }}
          >
            {[
              { id: "overview", title: "1. Policy Overview" },
              { id: "free-cancellation", title: "2. Free Cancellation" },
              { id: "partial-refund", title: "3. Partial Refund" },
              { id: "no-refund", title: "4. No Refund" },
              { id: "special-offers", title: "5. Special Offers" },
              { id: "group-bookings", title: "6. Group Bookings" },
              { id: "refund-process", title: "7. Refund Process" },
              { id: "contact", title: "8. Contact Support" },
            ].map((item, index) => (
              <li key={index}>
                <a
                  href={`#${item.id}`}
                  style={{
                    color: "#2E2E2E",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background =
                      "linear-gradient(135deg, rgba(238, 46, 36, 0.08) 0%, rgba(255, 196, 0, 0.08) 100%)";
                    e.target.style.transform = "translateX(5px)";
                    e.target.style.color = "#EE2E24";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.transform = "translateX(0)";
                    e.target.style.color = "#2E2E2E";
                  }}
                >
                  <FaCheckCircle
                    style={{ fontSize: "0.75rem", color: "#EE2E24" }}
                  />
                  {item.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* --- Sections --- */}
        <section id="overview" className="card p-4 mb-4 shadow-sm animate-fade">
          <div className="d-flex align-items-center mb-2">
            <span className="section-accent me-3"></span>
            <h2 className="card-title h4 mb-0 fw-bold">1. Policy Overview</h2>
          </div>
          <p className="text-dark text-start">
            At Trivana Hospitality, we understand that plans can change. Our
            cancellation policy is designed to be fair and transparent,
            balancing flexibility for our customers with the commitments we make
            to our service providers (hotels, restaurants, etc.). Please read
            the following terms carefully to understand how cancellations and
            refunds are handled for bookings made through Booktrivana.
          </p>
          <p className="mt-2 text-dark text-start">
            <strong>Important:</strong> Cancellation policies may vary by
            provider and booking type. Always check the specific cancellation
            terms shown during the booking process and on your confirmation.
          </p>
        </section>

        <section
          id="free-cancellation"
          className="card p-4 mb-4 shadow-sm animate-fade"
        >
          <div className="d-flex align-items-center mb-2">
            <span className="section-accent me-3"></span>
            <h2 className="card-title h4 mb-0 fw-bold">2. Free Cancellation</h2>
          </div>
          <div className="row align-items-center">
            <div className="col-12 col-md-9">
              <p className="text-dark text-start">
                <strong>Eligibility:</strong> Cancel your booking for free up to{" "}
                <strong>48 hours before check-in</strong>.
              </p>
              <p className="mt-2 text-dark text-start">
                <strong>Refund:</strong> No charges will be applied, and you
                will receive a full refund of the booking amount.
              </p>
              <p className="mt-2 text-dark text-start">
                <strong>How to Cancel:</strong> Log in to your account, go to
                "My Bookings," and select the cancellation option. You will
                receive a confirmation email once the cancellation is processed.
              </p>
            </div>
            <div className="col-12 col-md-3 text-center">
              <FaCheckCircle
                style={{ fontSize: "4rem", color: "#22C55E", opacity: 0.9 }}
              />
              <p
                className="mt-2 fw-bold"
                style={{ color: "#22C55E", fontSize: "0.9rem" }}
              >
                Full Refund
              </p>
            </div>
          </div>
        </section>

        <section
          id="partial-refund"
          className="card p-4 mb-4 shadow-sm animate-fade"
        >
          <div className="d-flex align-items-center mb-2">
            <span className="section-accent me-3"></span>
            <h2 className="card-title h4 mb-0 fw-bold">3. Partial Refund</h2>
          </div>
          <div className="row align-items-center">
            <div className="col-12 col-md-9">
              <p className="text-dark text-start">
                <strong>Eligibility:</strong> If you cancel between{" "}
                <strong>24-48 hours before check-in</strong>.
              </p>
              <p className="mt-2 text-dark text-start">
                <strong>Refund:</strong> 50% of the booking amount will be
                refunded. The remaining 50% is retained to cover provider
                commitments and processing fees.
              </p>
              <p className="mt-2 text-dark text-start">
                <strong>Processing Time:</strong> Partial refunds are typically
                processed within 5-7 business days to your original payment
                method.
              </p>
            </div>
            <div className="col-12 col-md-3 text-center">
              <FaExclamationTriangle
                style={{ fontSize: "4rem", color: "#F59E0B", opacity: 0.9 }}
              />
              <p
                className="mt-2 fw-bold"
                style={{ color: "#F59E0B", fontSize: "0.9rem" }}
              >
                50% Refund
              </p>
            </div>
          </div>
        </section>

        <section
          id="no-refund"
          className="card p-4 mb-4 shadow-sm animate-fade"
        >
          <div className="d-flex align-items-center mb-2">
            <span className="section-accent me-3"></span>
            <h2 className="card-title h4 mb-0 fw-bold">4. No Refund</h2>
          </div>
          <div className="row align-items-center">
            <div className="col-12 col-md-9">
              <p className="text-dark text-start">
                <strong>Eligibility:</strong> Cancellations made{" "}
                <strong>within 24 hours of check-in</strong> are non-refundable.
              </p>
              <p className="mt-2 text-dark text-start">
                <strong>Reason:</strong> At this point, providers have already
                allocated resources and declined other potential bookings.
                Last-minute cancellations prevent them from re-booking the
                room/table.
              </p>
              <p className="mt-2 text-dark text-start">
                <strong>No-Show Policy:</strong> Failure to show up for your
                reservation without prior cancellation will also result in no
                refund and may affect your ability to make future bookings.
              </p>
            </div>
            <div className="col-12 col-md-3 text-center">
              <FaTimesCircle
                style={{ fontSize: "4rem", color: "#EF4444", opacity: 0.9 }}
              />
              <p
                className="mt-2 fw-bold"
                style={{ color: "#EF4444", fontSize: "0.9rem" }}
              >
                Non-Refundable
              </p>
            </div>
          </div>
        </section>

        <section
          id="special-offers"
          className="card p-4 mb-4 shadow-sm animate-fade"
        >
          <div className="d-flex align-items-center mb-2">
            <span className="section-accent me-3"></span>
            <h2 className="card-title h4 mb-0 fw-bold">
              5. Special Offers &amp; Discounts
            </h2>
          </div>
          <p className="text-dark text-start">
            Bookings made under special promotional offers, flash sales, or
            heavily discounted rates are typically{" "}
            <strong>non-refundable</strong> unless explicitly stated otherwise
            at the time of booking.
          </p>
          <ul className="mt-2 text-dark text-start">
            <li>
              <strong>Promotional Bookings:</strong> These rates are offered at
              a reduced price in exchange for a firm commitment.
            </li>
            <li>
              <strong>Early Bird Discounts:</strong> May have specific
              cancellation terms; please review before confirming.
            </li>
            <li>
              <strong>Package Deals:</strong> Combined bookings (e.g., room +
              meal) may have different cancellation policies for each component.
            </li>
          </ul>
          <p className="mt-2 text-dark text-start">
            Always check the "Cancellation Policy" section during checkout for
            promotional bookings.
          </p>
        </section>

        <section
          id="group-bookings"
          className="card p-4 mb-4 shadow-sm animate-fade"
        >
          <div className="d-flex align-items-center mb-2">
            <span className="section-accent me-3"></span>
            <h2 className="card-title h4 mb-0 fw-bold">6. Group Bookings</h2>
          </div>
          <p className="text-dark text-start">
            Group bookings (defined as <strong>5 or more rooms</strong> or large
            table reservations) require special consideration due to the
            significant resources committed by providers.
          </p>
          <div className="row mt-3 g-3">
            <div className="col-12 col-lg-6">
              <h5 className="fw-bold text-dark text-start">
                Cancellation Timeline
              </h5>
              <p className="mt-2 text-dark text-start">
                Group bookings require cancellation at least{" "}
                <strong>7 days in advance</strong> for a full refund.
              </p>
            </div>
            <div className="col-12 col-lg-6">
              <h5 className="fw-bold text-dark text-start">
                Partial Cancellations
              </h5>
              <p className="mt-2 text-dark text-start">
                If only some rooms/tables are cancelled, the policy applies to
                each individual cancellation based on timing.
              </p>
            </div>
          </div>
          <p className="mt-3 text-dark text-start">
            For group bookings, we recommend contacting our support team
            directly at{" "}
            <a className="contact-link" href="mailto:booktrivana@gmail.com">
              booktrivana@gmail.com
            </a>{" "}
            to discuss your specific needs.
          </p>
        </section>

        <section
          id="refund-process"
          className="card p-4 mb-4 shadow-sm animate-fade"
        >
          <div className="d-flex align-items-center mb-2">
            <span className="section-accent me-3"></span>
            <h2 className="card-title h4 mb-0 fw-bold">7. Refund Processing</h2>
          </div>
          <p className="text-dark text-start">
            When you are eligible for a refund, the process works as follows:
          </p>
          <div className="row mt-3 g-3">
            <div className="col-12 col-md-6 col-lg-3">
              <div
                className="text-center p-3"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(238, 46, 36, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)",
                  borderRadius: "12px",
                  border: "1px solid rgba(238, 46, 36, 0.1)",
                }}
              >
                <FaClock
                  style={{ fontSize: "2.5rem", color: "#EE2E24", opacity: 0.8 }}
                />
                <h6
                  className="fw-bold mt-2 mb-1"
                  style={{ fontSize: "0.9rem" }}
                >
                  Step 1
                </h6>
                <p className="text-dark mb-0" style={{ fontSize: "0.85rem" }}>
                  Submit cancellation request
                </p>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div
                className="text-center p-3"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(238, 46, 36, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)",
                  borderRadius: "12px",
                  border: "1px solid rgba(238, 46, 36, 0.1)",
                }}
              >
                <FaCheckCircle
                  style={{ fontSize: "2.5rem", color: "#EE2E24", opacity: 0.8 }}
                />
                <h6
                  className="fw-bold mt-2 mb-1"
                  style={{ fontSize: "0.9rem" }}
                >
                  Step 2
                </h6>
                <p className="text-dark mb-0" style={{ fontSize: "0.85rem" }}>
                  Receive confirmation email
                </p>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div
                className="text-center p-3"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(238, 46, 36, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)",
                  borderRadius: "12px",
                  border: "1px solid rgba(238, 46, 36, 0.1)",
                }}
              >
                <FaMoneyBillWave
                  style={{ fontSize: "2.5rem", color: "#EE2E24", opacity: 0.8 }}
                />
                <h6
                  className="fw-bold mt-2 mb-1"
                  style={{ fontSize: "0.9rem" }}
                >
                  Step 3
                </h6>
                <p className="text-dark mb-0" style={{ fontSize: "0.85rem" }}>
                  Refund processed (5-7 days)
                </p>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div
                className="text-center p-3"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(238, 46, 36, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)",
                  borderRadius: "12px",
                  border: "1px solid rgba(238, 46, 36, 0.1)",
                }}
              >
                <FaCheckCircle
                  style={{ fontSize: "2.5rem", color: "#22C55E", opacity: 0.8 }}
                />
                <h6
                  className="fw-bold mt-2 mb-1"
                  style={{ fontSize: "0.9rem" }}
                >
                  Step 4
                </h6>
                <p className="text-dark mb-0" style={{ fontSize: "0.85rem" }}>
                  Funds returned to account
                </p>
              </div>
            </div>
          </div>
          <p className="mt-3 text-dark text-start">
            <strong>Processing Time:</strong> Refunds are typically processed
            within 5-7 business days. The time it takes for funds to appear in
            your account may vary depending on your bank or payment provider.
          </p>
          <p className="mt-2 text-dark text-start">
            <strong>Refund Method:</strong> Refunds will be issued to the
            original payment method used for the booking.
          </p>
        </section>

        <section id="contact" className="card p-4 mb-4 shadow-sm animate-fade">
          <div className="d-flex align-items-center mb-2">
            <span className="section-accent me-3"></span>
            <h2 className="card-title h4 mb-0 fw-bold">8. Contact Support</h2>
          </div>
          <p className="text-dark text-start">
            If you have questions about our cancellation policy or need
            assistance with a booking cancellation, our support team is here to
            help:
          </p>
          <p className="mt-2 text-dark text-start">
            <strong>Trivana Hospitality (Booktrivana)</strong>
            <br />
            Email:{" "}
            <a className="contact-link" href="mailto:booktrivana@gmail.com">
              booktrivana@gmail.com
            </a>
            <br />
            Phone: +91 9257734350 (support hours: 9 AM - 9 PM IST, 7 days a
            week)
          </p>

          <p className="mt-3 text-muted text-start">
            Note: This cancellation policy is subject to change. We will post
            updates on this page. For the most current policy applicable to your
            booking, please refer to your booking confirmation email.
          </p>
        </section>
      </div>

      {/* Custom CSS for features not in Bootstrap */}
      <style>{`
        /* Global & overflow fixes (RESPO PROBLEM safe) */
        html,
        body {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          font-family: "Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          overflow-x: hidden;
          scroll-behavior: smooth;
        }

        *,
        *::before,
        *::after {
          box-sizing: inherit;
        }

        /* Page wrapper & background */
        .cancellation-wrapper {
          min-height: 100vh;
          width: 100%;
          background: linear-gradient(180deg, #ffffff 0%, #f5f5f5 100%);
          overflow-x: hidden;
        }

        /* Animated gradient heading (Trivana theme) */
        .header-title {
          font-size: clamp(22px, 3.4vw, 40px);
          font-weight: 800;
          line-height: 1.05;
          margin: 0 0 8px 0;
          background: linear-gradient(90deg, #EE2E24, #FFC400, #EE2E24);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          display: inline-block;
          animation: slideInFromTop 600ms ease both;
        }

        .subhead {
          color: #475569;
          font-size: clamp(14px, 1.6vw, 18px);
          margin: 0 auto;
          max-width: 900px;
        }
        
        /* Card hover animation */
        .card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .card:hover {
          transform: translateY(-6px);
          box-shadow: 0 18px 40px rgba(14, 30, 37, 0.08);
        }

        /* Section headings inside cards */
        .section-accent {
          width: 6px;
          height: 26px;
          border-radius: 4px;
          background: linear-gradient(180deg, #EE2E24, #FFC400);
          flex-shrink: 0;
        }
        
        /* Table of contents and contact links */
        .toc-link,
        .contact-link {
          color: #0f172a; /* Default link color */
          font-weight: 600;
          text-decoration: none;
          transition: color 0.2s ease;
        }
        
        .toc-link:hover,
        .contact-link:hover {
          color: #EE2E24; /* Brand hover color */
          text-decoration: underline;
        }

        /* small utilities and responsive fixes */
        .muted {
          color: #6b7280;
        }

        /* anchor offset for sticky headers if any */
        :target {
          scroll-margin-top: 90px;
        }

        /* Animations */
        @keyframes slideInFromTop {
          from {
            opacity: 0;
            transform: translateY(-12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade {
          animation: fadeUp 600ms ease both;
        }
        `}</style>
    </div>
  );
};

export default CancellationPolicy;
